'use client';
import { useEffect, useMemo, useState } from 'react';
import { TrendingUp } from 'lucide-react';

/**
 * Traffic TAB（P0 行銷基建）：Track A 聚合流量儀表板
 * 視覺風格與 AnalyticsTab 一致（手寫 SVG、無圖表庫）。
 * 數據源：Worker GET /stats?days=30（純聚合，無個人資料）。
 */

const CF_WORKER_URL = 'https://jkd-whatsapp-verification.jimsbond007.workers.dev';
const GOLD = '#E8FF00';
const GREEN = '#22C55E';
const BLUE = '#3B82F6';
const PURPLE = '#A855F7';
const GRAY = '#737373';

interface StatsResponse {
  success: boolean;
  days: number;
  daily: { date: string; pv: number }[];
  totals: {
    pv?: { total?: number; page?: Record<string, number>; locale?: Record<string, number>; country?: Record<string, number> };
    ref?: Record<string, number>;
    utm?: Record<string, number>;
    event?: Record<string, number>;
  };
}

function topN(record: Record<string, number> | undefined, n: number): [string, number][] {
  if (!record) return [];
  return Object.entries(record).sort((a, b) => b[1] - a[1]).slice(0, n);
}

/** 巢狀計數樹 → 扁平 [path, value]（utm 為 source.medium.campaign 三層；其他樹同理） */
function flattenTree(record: unknown, prefix = ''): [string, number][] {
  const out: [string, number][] = [];
  if (!record || typeof record !== 'object') return out;
  for (const [k, v] of Object.entries(record as Record<string, unknown>)) {
    const label = prefix ? `${prefix} / ${k}` : k;
    if (v && typeof v === 'object') out.push(...flattenTree(v, label));
    else if (typeof v === 'number') out.push([label, v]);
  }
  return out.sort((a, b) => b[1] - a[1]);
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card-dark">
      <p className="text-[11px] font-mono uppercase tracking-wider text-gray-500">{label}</p>
      <p className="text-3xl font-black mt-2 text-white">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function BarList({ items, color = GOLD, empty }: { items: [string, number][]; color?: string; empty: string }) {
  const max = Math.max(1, ...items.map(([, v]) => v));
  if (items.length === 0) return <p className="text-sm text-gray-500">{empty}</p>;
  return (
    <ul className="space-y-2">
      {items.map(([key, v]) => (
        <li key={key} className="flex items-center gap-3 text-sm">
          <span className="w-44 shrink-0 truncate font-mono text-xs text-gray-300" title={key}>{key}</span>
          <span className="flex-1 h-2.5 bg-jkd-gray-100/30 rounded overflow-hidden">
            <span className="block h-full rounded" style={{ width: `${(v / max) * 100}%`, backgroundColor: color }} />
          </span>
          <span className="font-mono text-gray-400 w-12 text-right">{String(v)}</span>
        </li>
      ))}
    </ul>
  );
}

function PvLine({ daily }: { daily: { date: string; pv: number }[] }) {
  const W = 640;
  const H = 220;
  const padL = 36;
  const padR = 12;
  const padT = 14;
  const padB = 28;
  const iw = W - padL - padR;
  const ih = H - padT - padB;
  const max = Math.max(1, ...daily.map(d => d.pv));
  const x = (i: number) => padL + (daily.length <= 1 ? iw / 2 : (i / (daily.length - 1)) * iw);
  const y = (v: number) => padT + ih - (v / max) * ih;
  const line = daily.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d.pv).toFixed(1)}`).join(' ');
  const area = `${line} L${x(daily.length - 1).toFixed(1)},${(padT + ih).toFixed(1)} L${x(0).toFixed(1)},${(padT + ih).toFixed(1)} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="PV trend">
      <defs>
        <linearGradient id="pvArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={GOLD} stopOpacity="0.35" />
          <stop offset="100%" stopColor={GOLD} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {[0, 0.5, 1].map(f => (
        <g key={f}>
          <line x1={padL} x2={W - padR} y1={y(max * f)} y2={y(max * f)} stroke="#2D2D2D" strokeWidth="1" />
          <text x={padL - 6} y={y(max * f) + 3.5} textAnchor="end" fill={GRAY} fontSize="10">{Math.round(max * f)}</text>
        </g>
      ))}
      <path d={area} fill="url(#pvArea)" />
      <path d={line} fill="none" stroke={GOLD} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {daily.map((d, i) => (
        <g key={d.date}>
          <circle cx={x(i)} cy={y(d.pv)} r="2.5" fill={GOLD} />
          {i % Math.ceil(daily.length / 10) === 0 && (
            <text x={x(i)} y={H - 8} textAnchor="middle" fill={GRAY} fontSize="9">{d.date.slice(5)}</text>
          )}
        </g>
      ))}
    </svg>
  );
}

function Funnel({ pv, formSubmits, verified }: { pv: number; formSubmits: number; verified: number }) {
  const stages = [
    { label: 'Pageviews', value: pv, color: GOLD },
    { label: 'Form Submits', value: formSubmits, color: BLUE },
    { label: 'Verified Leads', value: verified, color: GREEN },
  ];
  const max = Math.max(1, pv);
  return (
    <div className="space-y-3">
      {stages.map((s, i) => (
        <div key={s.label}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-300">{s.label}</span>
            <span className="font-mono" style={{ color: s.color }}>
              {s.value}
              {i > 0 && s.value > 0 && stages[i - 1].value > 0 && (
                <span className="text-gray-500 ml-2">{Math.round((s.value / stages[i - 1].value) * 100)}%</span>
              )}
            </span>
          </div>
          <div className="h-3 bg-jkd-gray-100/30 rounded overflow-hidden">
            <div className="h-full rounded" style={{ width: `${Math.max(s.value > 0 ? 2 : 0, (s.value / max) * 100)}%`, backgroundColor: s.color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TrafficTab() {
  const [data, setData] = useState<StatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${CF_WORKER_URL}/stats?days=${days}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(setData)
      .catch(err => setError(err instanceof Error ? err.message : '載入失敗'))
      .finally(() => setLoading(false));
  }, [days]);

  const leadCounts = useMemo(() => {
    // 轉化漏斗的表單數來自 track 事件；已驗證數由 /leads 取
    return { formSubmits: (data?.totals.event?.contact_submit || 0) + (data?.totals.event?.diagnosis_submit || 0) };
  }, [data]);

  const [verifiedCount, setVerifiedCount] = useState<number | null>(null);
  useEffect(() => {
    fetch(`${CF_WORKER_URL}/leads`)
      .then(res => res.json())
      .then(d => setVerifiedCount((d.leads || []).filter((l: { verified: boolean }) => l.verified).length))
      .catch(() => setVerifiedCount(null));
  }, []);

  if (loading) return <div className="text-center py-20 text-gray-500">載入中…</div>;
  if (error || !data) {
    return (
      <div className="card-dark text-center py-16">
        <TrendingUp className="w-10 h-10 text-jkd-gold/50 mx-auto mb-4" />
        <p className="text-gray-400">Traffic 數據載入失敗：{error}</p>
      </div>
    );
  }

  const totalPv = data.totals.pv?.total || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {[14, 30].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-4 py-1.5 rounded text-sm font-bold transition-colors ${
                days === d ? 'bg-jkd-gold text-jkd-black' : 'bg-jkd-gray-100/40 text-gray-300 hover:text-white'
              }`}
            >
              {d} 日
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500 font-mono">聚合數據 · 無個人資料</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Pageviews" value={String(totalPv)} sub={`近 ${data.days} 日`} />
        <Kpi label="Form Submits" value={String(leadCounts.formSubmits)} sub="contact + diagnosis" />
        <Kpi label="Verified Leads" value={verifiedCount === null ? '—' : String(verifiedCount)} sub="WhatsApp 已驗證" />
        <Kpi
          label="日均 PV"
          value={String(Math.round(totalPv / Math.max(1, data.days)))}
          sub="按追蹤期平均"
        />
      </div>

      <div className="card-dark">
        <h2 className="text-lg font-bold text-white mb-4">Pageview 趨勢</h2>
        <PvLine daily={data.daily} />
      </div>

      <div className="card-dark">
        <h2 className="text-lg font-bold text-white mb-4">轉化漏斗</h2>
        <Funnel pv={totalPv} formSubmits={leadCounts.formSubmits} verified={verifiedCount || 0} />
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div className="card-dark">
          <h2 className="text-lg font-bold text-white mb-4">熱門頁面 Top 10</h2>
          <BarList items={topN(data.totals.pv?.page, 10)} empty="暫無頁面數據" />
        </div>
        <div className="card-dark">
          <h2 className="text-lg font-bold text-white mb-4">訪客國別 Top 10</h2>
          <BarList items={topN(data.totals.pv?.country, 10)} color={PURPLE} empty="暫無國別數據" />
        </div>
        <div className="card-dark">
          <h2 className="text-lg font-bold text-white mb-4">語言分佈</h2>
          <BarList items={topN(data.totals.pv?.locale, 10)} color={BLUE} empty="暫無語言數據" />
        </div>
        <div className="card-dark">
          <h2 className="text-lg font-bold text-white mb-4">來源 Top 10</h2>
          <BarList items={topN(data.totals.ref, 10)} color={GREEN} empty="暫無來源數據" />
        </div>
        <div className="card-dark">
          <h2 className="text-lg font-bold text-white mb-4">UTM Campaigns</h2>
          <BarList items={flattenTree(data.totals.utm).slice(0, 12)} empty="暫無 UTM 數據（帖文带上 utm_source 即可歸因）" />
        </div>
        <div className="card-dark">
          <h2 className="text-lg font-bold text-white mb-4">轉化事件</h2>
          <BarList items={topN(data.totals.event, 10)} empty="暫無事件數據" />
        </div>
      </div>
    </div>
  );
}
